import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { PhoneBookRequest } from '../../types';
import { Loading } from '../../components/ui/Loading';
import { Empty } from '../../components/ui/Empty';
import { ErrorAlert } from '../../components/ui/ErrorAlert';
import { StatusBadge } from '../../components/StatusBadge';
import { displayPhone, normalizePhone } from '../../utils/phone';
import { formatShivar } from '../../utils/shivar';
import { syncToGoogleSheets } from '../../utils/googleSheets';

export const EmployeeRequests = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState<PhoneBookRequest[]>([]);
  const [shivars, setShivars] = useState<{ value: string; label: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [selected, setSelected] = useState<PhoneBookRequest | null>(null);
  const [draft, setDraft] = useState<Partial<PhoneBookRequest> | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const loadRequests = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: dbError } = await supabase
        .from('phone_book_requests')
        .select('*')
        .order('created_at', { ascending: false });
      if (dbError) throw dbError;
      const list = (data ?? []) as PhoneBookRequest[];
      setRequests(
        list.filter(
          (r) => r.status === 'UNDER_REVIEW' || (r.status === 'APPROVED' && r.reviewed_by === user?.id)
        )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  useEffect(() => {
    const loadShivars = async () => {
      const { data, error: dbError } = await supabase.from('shivars').select('name, marathi_name').order('name');
      if (!dbError) {
        setShivars((data ?? []).map((s) => ({ value: s.name, label: formatShivar(s.name, s.marathi_name) })));
      }
    };
    loadShivars();
  }, []);

  useEffect(() => {
    if (selected) {
      setDraft({ ...selected });
    } else {
      setDraft(null);
    }
  }, [selected]);

  const handleDraftChange = (name: string, value: string) => {
    setDraft((prev) => (prev ? { ...prev, [name]: value } as Partial<PhoneBookRequest> : prev));
  };

  const handleApprove = async () => {
    if (!selected || !draft) return;
    setProcessing(true);
    try {
      const rawShivar = (draft.shivar ?? selected.shivar ?? '').trim();
      const isOther = rawShivar === '__other__';
      const effectiveShivar = isOther ? 'Other' : rawShivar;
      const normalized = normalizePhone(draft.phone_number || selected.phone_number);
      const alternate = draft.alternate_phone_number ? normalizePhone(draft.alternate_phone_number) : null;

      const { data: inserted, error: insertError } = await supabase.from('phone_book').insert({
        marathi_name: draft.marathi_name || selected.marathi_name,
        name: draft.name || selected.name,
        phone_number: normalized,
        alternate_phone_number: alternate,
        shivar: effectiveShivar || null,
        village: isOther ? draft.village || null : null,
        taluka: isOther ? draft.taluka || null : null,
        district: isOther ? draft.district || null : null,
        address: isOther ? draft.address || null : null,
        category: isOther ? draft.category || null : null,
        status: 'APPROVED',
        created_by: selected.requested_by,
        approved_by: user?.id ?? null,
        approved_at: new Date().toISOString(),
      }).select();
      if (insertError) throw insertError;
      if (inserted && inserted.length > 0) {
        await syncToGoogleSheets(inserted[0] as Record<string, unknown>);
      }

      const { error: updateError } = await supabase
        .from('phone_book_requests')
        .update({
          status: 'APPROVED',
          reviewed_by: user?.id,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', selected.id);
      if (updateError) throw updateError;

      setSelected(null);
      setDraft(null);
      await loadRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Approval failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (request: PhoneBookRequest) => {
    setProcessing(true);
    try {
      const { error: updateError } = await supabase
        .from('phone_book_requests')
        .update({
          status: 'BLOCKED',
          reviewed_by: user?.id,
          reviewed_at: new Date().toISOString(),
          rejection_reason: rejectionReason,
        })
        .eq('id', request.id);
      if (updateError) throw updateError;

      setSelected(null);
      setRejectionReason('');
      await loadRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Rejection failed');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) return <Loading full />;
  if (error) return <ErrorAlert message={error} onRetry={loadRequests} />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Contact Requests</h1>
        <p className="text-sm text-gray-600">Review and approve user-submitted contact requests.</p>
      </div>
      {requests.length === 0 ? (
        <Empty title="No requests" message="No contact requests to show." />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Marathi Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Phone</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Village</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Notes</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {requests.map((request) => (
                <tr key={request.id}>
                  <td className="px-4 py-3 text-sm font-medium text-blue-800" lang="mr">{request.marathi_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{request.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{displayPhone(request.phone_number)}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{request.village ?? '-'}</td>
                  <td className="px-4 py-3"><StatusBadge status={request.status} /></td>
                  <td className="px-4 py-3 text-sm text-gray-600">{request.notes ?? '-'}</td>
                  <td className="px-4 py-3 text-right">
                    {request.status === 'UNDER_REVIEW' && (
                      <button
                        onClick={() => setSelected(request)}
                        className="rounded bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700"
                      >
                        Review
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {selected && draft && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <h2 className="text-lg font-bold text-gray-900">Review Request</h2>
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Marathi Name</label>
              <input
                value={draft.marathi_name ?? ''}
                onChange={(e) => handleDraftChange('marathi_name', e.target.value)}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Name</label>
              <input
                value={draft.name ?? ''}
                onChange={(e) => handleDraftChange('name', e.target.value)}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Phone</label>
              <input
                value={draft.phone_number ?? ''}
                onChange={(e) => handleDraftChange('phone_number', e.target.value)}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Alternate Phone</label>
              <input
                value={draft.alternate_phone_number ?? ''}
                onChange={(e) => handleDraftChange('alternate_phone_number', e.target.value)}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Shivar</label>
              <select
                value={draft.shivar ?? ''}
                onChange={(e) => handleDraftChange('shivar', e.target.value)}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="">Select shivar</option>
                {shivars.map((shivar) => (
                  <option key={shivar.value} value={shivar.value}>{shivar.label}</option>
                ))}
                <option value="__other__">Other</option>
              </select>
            </div>
            {draft.shivar === '__other__' && (
              <>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Village</label>
                  <input
                    value={draft.village ?? ''}
                    onChange={(e) => handleDraftChange('village', e.target.value)}
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Taluka</label>
                  <input
                    value={draft.taluka ?? ''}
                    onChange={(e) => handleDraftChange('taluka', e.target.value)}
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">District</label>
                  <input
                    value={draft.district ?? ''}
                    onChange={(e) => handleDraftChange('district', e.target.value)}
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-gray-700">Address</label>
                  <textarea
                    value={draft.address ?? ''}
                    onChange={(e) => handleDraftChange('address', e.target.value)}
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                    rows={2}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Category</label>
                  <input
                    value={draft.category ?? ''}
                    onChange={(e) => handleDraftChange('category', e.target.value)}
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              </>
            )}
            <div className="md:col-span-2">
              <label className="mb-1 block text-sm font-medium text-gray-700">Rejection reason (required for block)</label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                rows={2}
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button
              onClick={() => { setSelected(null); setDraft(null); }}
              className="rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => handleReject(selected)}
              disabled={processing || !rejectionReason.trim()}
              className="rounded bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              Block
            </button>
            <button
              onClick={handleApprove}
              disabled={processing}
              className="rounded bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
            >
              Approve
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
